// src/contexts/DraftsContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import { useStore } from "./StoreContext";
import { api, draftsApi, saveDraftsBulkOnUnload } from "../services/api";
import { toast } from "react-toastify";

// Draft interface
export interface Draft {
  asin: string;
  supplier: string;
  region: string;
  title?: string;
  price?: number | null;
  price_str?: string | null;
  image_url?: string | null;
  images?: string[];
  url?: string;
  status?: string;      // "idle", "loading", "success", "error", "publishing", "published"
  scraped?: boolean;
  error?: string;
  description?: string;
  availability?: string;
  stock_status?: string;
  stock_message?: string;
  prime?: boolean;
  vendor?: string;
  dispatch_from_amazon?: boolean;
  featureBullets?: string;
  technical_details?: Record<string, any>;
  product_info?: Record<string, any>;
  brand?: string;
  mpn?: string;
  weight?: string;
  dimensions?: string;
  category?: string;
  ebayCategory?: string;
  primaryCategoryId?: string;
  aiCategoryConfidence?: number | null;
  ebayItemId?: string;
  publishedAt?: string;
  needsManualCategory?: boolean;
  created_at?: string;
  updated_at?: string;
  // Variation fields
  has_variations?: boolean;
  variation_attributes?: string[];
  variations?: any[];
  selected_variation?: any;
  // AI Title Optimization
  ai_status?: string | null;
  ai_optimized_at?: string | null;
  ai_original_title?: string | null;
  ai_error?: string | null;
  [key: string]: any;   // Other dynamic fields
}

interface DraftsContextType {
  drafts: Draft[];
  loading: boolean;
  addDrafts: (draftArray: Draft[]) => void;
  updateDraft: (asin: string, update: Partial<Draft>) => void;
  updateDraftLocal: (asin: string, update: Partial<Draft>) => void;
  removeDraft: (asin: string) => void;
  removeManyDrafts: (asins: string[], onlyCompleted?: boolean) => void;
  clearAllDrafts: () => void;
  refreshDrafts: () => Promise<void>;
  bulkQueueRef: React.MutableRefObject<Array<{ asin: string; data: any }>>;
  flushQueue: () => Promise<void>;
}

const DraftsContext = createContext<DraftsContextType | null>(null);

export function DraftsProvider({ children }: { children: React.ReactNode }) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { token, isAuthenticated } = useAuth();
  const { selectedStore } = useStore();
  const lastLoadedStoreRef = useRef<number | null>(null);
  const bulkQueueRef = useRef<Array<{ asin: string; data: any }>>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load drafts from backend on mount
  const loadDraftsFromBackend = useCallback(async (storeId?: number) => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      const data = await api.get<any>(`/api/drafts/list${storeParam}`);

      if (data.success && data.drafts) {
          // Map backend data to Draft interface
          const loadedDrafts: Draft[] = data.drafts.map((d: any) => ({
            asin: d.asin,
            supplier: d.supplier || "amazon",
            region: d.region || "UK",
            title: d.title,
            price: d.price,
            price_str: d.price_str,
            image_url: d.image_url,
            images: d.images || [],
            status: d.status || "idle",
            scraped: d.scraped || false,
            error: d.error,
            description: d.description,
            featureBullets: d.featureBullets,
            technical_details: d.technical_details,
            product_info: d.product_info,
            brand: d.brand,
            category: d.category,
            ebayCategory: d.ebayCategory,
            primaryCategoryId: d.primaryCategoryId,
            aiCategoryConfidence: d.aiCategoryConfidence,
            ebayItemId: d.ebayItemId,
            stock_status: d.stock_status,
            stock_message: d.stock_message,
            // Variation data
            has_variations: d.has_variations || false,
            variation_attributes: d.variation_attributes || [],
            variations: d.variations || [],
            selected_variation: d.selected_variation,
            best_fba_price: d.best_fba_price,
            best_fba_seller: d.best_fba_seller,
            best_fba_condition: d.best_fba_condition,
            aod_offers: d.aod_offers || [],
            // IS9 (2026-08-16): scraper/kategori alanlari — ISTEMCI BUNLARI
            // YONETMIYOR ama RENDER ICIN GEREKIYOR. Daha once bu mapping'de
            // yoklardi: F5 sonrasi draft nesnesi 37 alana duserdi ve
            //   - "Only N left in stock" metni (amazon_qty gerekiyor)
            //   - prime rozeti (dispatch_from_amazon / fba_count gerekiyor)
            // render EDILEMEZDI. Veri DB'de eksiksizdi; sorun tasimaydi.
            // amazon_qty / fba_count / oos_reason backend ZATEN gonderiyordu,
            // burada atiliyordu; kalan 15'i IS9'da liste ucuna eklendi.
            // riskWarning: buradaki eksikligi index.tsx:456 efektinin her
            // sayfa yuklemesinde yeniden yazmasina sebep oluyordu (kendini
            // besleyen updateDraft dongusu) -> tasinmasi donguyu bitirir.
            amazon_qty: d.amazon_qty,
            fba_count: d.fba_count,
            oos_reason: d.oos_reason,
            amazon_price: d.amazon_price,
            availability: d.availability,
            category_decided_at: d.category_decided_at,
            category_source: d.category_source,
            delivery_days: d.delivery_days,
            dimensions: d.dimensions,
            dispatch_from_amazon: d.dispatch_from_amazon,
            is_international: d.is_international,
            method: d.method,
            mpn: d.mpn,
            prime: d.prime,
            riskWarning: d.riskWarning,
            url: d.url,
            vendor: d.vendor,
            weight: d.weight,
            // AI Title Optimization metadata (backend Faz 1A)
            ai_status: d.ai_status,
            ai_optimized_at: d.ai_optimized_at,
            ai_original_title: d.ai_original_title,
            ai_error: d.ai_error,
            created_at: d.created_at,
            updated_at: d.updated_at
          }));
          setDrafts(loadedDrafts);
          console.log(`[DraftsContext] Loaded ${loadedDrafts.length} drafts from backend`);
        }
    } catch (error) {
      console.error("[DraftsContext] Error loading drafts:", error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  // Load drafts when auth or selected store changes
  useEffect(() => {
    if (isAuthenticated && token) {
      const storeChanged = lastLoadedStoreRef.current !== selectedStore?.id;
      if (storeChanged || drafts.length === 0) {
        lastLoadedStoreRef.current = selectedStore?.id || null;
        loadDraftsFromBackend(selectedStore?.id);
      }
    } else {
      setDrafts([]);
      setLoading(false);
    }
  }, [isAuthenticated, token, selectedStore?.id, loadDraftsFromBackend]);

  // Flush bulk queue: queue'daki tum draft'lari /api/drafts/save-bulk'a gonderir
  const flushQueue = useCallback(async () => {
    if (bulkQueueRef.current.length === 0) return;
    const batch = bulkQueueRef.current.splice(0, bulkQueueRef.current.length);
    try {
      await draftsApi.saveDraftsBulk({ drafts: batch });
      console.log(`[BulkQueue] Flushed ${batch.length} drafts`);
    } catch (e) {
      bulkQueueRef.current.unshift(...batch);
      console.error("[BulkQueue] Flush failed, re-queued:", e);
    }
  }, []);

  // beforeunload: queue'yu keepalive fetch ile bosalt (tab kapanma / F5 / provider unmount)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (bulkQueueRef.current.length > 0) {
        // [K1-UNLOAD] sendBeacon goreli adrese (app vhost, 405) ve basliksiz gidiyordu -> kayip.
        saveDraftsBulkOnUnload(bulkQueueRef.current);
        bulkQueueRef.current = [];
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, []);

  // Local-only state update — does NOT persist to backend
  const updateDraftLocal = useCallback((asin: string, update: Partial<Draft>) => {
    setDrafts(prev => prev.map(d => d.asin === asin ? { ...d, ...update } : d));
  }, []);

  // Delete draft from backend
  const deleteDraftFromBackend = useCallback(async (asin: string) => {
    if (!isAuthenticated || !token) return;

    try {
      await api.delete(`/api/drafts/delete/${asin}`);
    } catch (error) {
      console.error("[DraftsContext] Error deleting draft:", error);
    }
  }, [isAuthenticated, token]);

  // Delete multiple drafts from backend
  const deleteManyDraftsFromBackend = useCallback(async (asins: string[], onlyCompleted = false) => {
    if (!isAuthenticated || !token || asins.length === 0) return;

    try {
      const data = await api.post<any>('/api/drafts/delete-many', {
        asins,
        only_completed: onlyCompleted
      });

      // Count mismatch detection - backend may filter by status
      if (data.deleted !== asins.length) {
        console.warn(
          `[DraftsContext] Delete mismatch: requested ${asins.length}, deleted ${data.deleted}. ` +
          (onlyCompleted
            ? `Backend filtered non-completed drafts (expected for Clear Completed).`
            : `Backend filtered some drafts. Refreshing from server...`)
        );
        // Sync state with backend
        await loadDraftsFromBackend(selectedStore?.id);
      }

      return data;
    } catch (error) {
      console.error("[DraftsContext] Error deleting drafts:", error);
    }
  }, [isAuthenticated, token, loadDraftsFromBackend, selectedStore]);

  // Add drafts (local + backend)
  const addDrafts = useCallback((draftArray: Draft[]) => {
    setDrafts(prev => {
      // Filter out duplicates
      const existingAsins = new Set(prev.map(d => d.asin));
      const newDrafts = draftArray.filter(d => !existingAsins.has(d.asin));
      return [...prev, ...newDrafts];
    });

    // Bulk add: N draft → 1 POST /api/drafts/add (sadece kabuk)
    if (draftArray.length > 0) {
      const bulkPayload = draftArray.map(d => ({
        asin: d.asin,
        supplier: d.supplier || "amazon",
        region: d.region || "UK",
      }));
      // Fire and forget — add endpoint sadece boş satır oluşturur,
      // tam veri scrape sonrası /save-bulk ile gelir
      draftsApi.addDrafts(bulkPayload).catch(err => {
        console.error("[DraftsContext] Bulk add failed:", err);
      });
    }
  }, []);

  // Update draft (local + bulkQueueRef push, flush trigger outside setState)
  const updateDraft = useCallback((asin: string, update: Partial<Draft>) => {
    let shouldFlush = false;

    setDrafts(prev => prev.map(d => {
      if (d.asin === asin) {
        const newDraft = { ...d, ...update };
        bulkQueueRef.current.push({ asin, data: newDraft });
        shouldFlush = true;
        return newDraft;
      }
      return d;
    }));

    // Flush trigger: setState dışında — pure setState callback için React Strict Mode uyumu
    if (shouldFlush) {
      if (bulkQueueRef.current.length >= 5) {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        flushQueue();
      } else {
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flushTimerRef.current = setTimeout(flushQueue, 3000);
      }
    }
  }, [flushQueue]);

  // Remove draft (local + backend)
  const removeDraft = useCallback((asin: string) => {
    // Purge queue — tek silme akışında da queue temizlenmeli (simetri)
    bulkQueueRef.current = bulkQueueRef.current.filter(
      e => e.asin.toUpperCase() !== asin.toUpperCase()
    );
    setDrafts(drafts => drafts.filter(d => d.asin !== asin));
    deleteDraftFromBackend(asin);
  }, [deleteDraftFromBackend]);

  // Remove many drafts (local + backend)
  const removeManyDrafts = useCallback((asins: string[], onlyCompleted = false) => {
    // Purge queue — debounce/beforeunload flush bu ASIN'leri re-create etmesin
    const asinSet = new Set(asins.map(a => a.toUpperCase()));
    bulkQueueRef.current = bulkQueueRef.current.filter(
      e => !asinSet.has(e.asin.toUpperCase())
    );
    setDrafts(drafts => drafts.filter(d => !asins.includes(d.asin)));
    deleteManyDraftsFromBackend(asins, onlyCompleted);
  }, [deleteManyDraftsFromBackend]);

  // Clear all drafts (local + backend)
  const clearAllDrafts = useCallback(async () => {
    setDrafts([]);

    if (!isAuthenticated || !token) return;

    try {
      await api.delete('/api/drafts/clear');
    } catch (error) {
      console.error("[DraftsContext] Error clearing drafts:", error);
    }
  }, [isAuthenticated, token]);

  // Refresh drafts from backend
  const refreshDrafts = useCallback(async () => {
    await loadDraftsFromBackend(selectedStore?.id);
  }, [loadDraftsFromBackend, selectedStore?.id]);

  return (
    <DraftsContext.Provider value={{
      drafts,
      loading,
      addDrafts,
      updateDraft,
      updateDraftLocal,
      removeDraft,
      removeManyDrafts,
      clearAllDrafts,
      refreshDrafts,
      bulkQueueRef,
      flushQueue
    }}>
      {children}
    </DraftsContext.Provider>
  );
}

export function useDrafts() {
  const context = useContext(DraftsContext);
  if (!context) {
    throw new Error("useDrafts must be used within a DraftsProvider");
  }
  return context;
}
